/**
 * O texto de uma mensagem, com os endereços clicáveis (C02).
 *
 * ── Por que não `dangerouslySetInnerHTML` ─────────────────────────────────
 *
 * É a forma mais curta de fazer isto, e é a que transforma toda mensagem de
 * cliente em código executável na nossa tela. O conteúdo aqui vem de fora:
 * quem escreve é o contato, pelo WhatsApp, pelo Instagram, por onde for.
 *
 * Então o texto nunca vira HTML. Ele é PARTIDO em pedaços, e o React desenha
 * cada pedaço como texto ou como `<a>` — o React escapa o que desenha, e um
 * `<script>` escrito pelo cliente aparece na tela como as letras que são. Quem
 * desenha é o `TextoFormatado` (#615); daqui sai o `partir`, que acha os
 * endereços dentro de cada pedaço.
 *
 * ── Só `http` e `https` ───────────────────────────────────────────────────
 *
 * `javascript:alert(1)` é um esquema válido de URL, e um `<a href>` com ele
 * executa no clique. `data:text/html,...` abre uma página que o cliente
 * escreveu, na nossa origem, para quem clicou.
 *
 * A lista é fechada de propósito: qualquer coisa que não seja `http`, `https`
 * ou um `www.` no começo continua sendo texto comum.
 *
 * ── A pontuação final não faz parte do endereço ───────────────────────────
 *
 * "veja em https://exemplo.com." termina com um ponto que é da frase, não do
 * endereço. O mesmo vale para vírgula, parêntese e aspas — e um link que leva
 * ao lugar errado por um caractere é pior que nenhum link, porque parece
 * defeito do site de destino.
 */

/**
 * Os finais que fazem de um nome um endereço.
 *
 * ── Por que uma lista, e não "qualquer coisa depois do ponto" ─────────────
 *
 * A primeira versão só reconhecia `https://…` e `www.…`, e deixava
 * `google.com.br` seco de fora — que é exatamente como as pessoas escrevem.
 *
 * O outro extremo, aceitar qualquer `palavra.palavra`, transforma
 * `arquivo.zip`, `foto.png` e `contrato.pdf` em links quebrados no meio de uma
 * conversa de vendas, onde nome de arquivo aparece o tempo todo.
 *
 * A lista resolve os dois: `zip`, `png` e `pdf` não estão nela, e `com.br`
 * também não precisa estar — `br` cobre, porque a parte da frente aceita
 * quantos rótulos vierem.
 *
 * `ai` fica DE FORA de propósito: é domínio válido, e também a extensão do
 * Illustrator. Num negócio que troca arte de anúncio, `logo.ai` é mais provável
 * que um site `.ai` — e link errado parece defeito do destino, não nosso.
 */
const FINAIS =
  'com|br|net|org|gov|edu|io|dev|app|me|co|info|biz|tv|xyz|online|site|shop|store|blog|cloud|tech|com\\.br';

/**
 * O que reconhecemos como endereço, em três formas.
 *
 * Com esquema (`https://…`), com `www.` e o nome seco terminado num dos finais
 * acima — este último podendo levar caminho e busca (`google.com/algo?x=1`).
 *
 * O que vem ANTES importa: `(?<![A-Za-z0-9@.-])` impede casar no meio de um
 * token, e é o que mantém `fulano@empresa.com.br` sendo um e-mail, e não um
 * link para `empresa.com.br`.
 *
 * O `_` é o caso à parte: sozinho na frente, é o itálico do WhatsApp abrindo
 * (`_veja google.com_`, #615), e o endereço começa depois dele. Grudado numa
 * palavra (`arquivo_google.com`), continua sendo meio de token.
 */
const ENDERECO = new RegExp(
  String.raw`(?<![A-Za-z0-9@.-])(?<!\w_)(?:https?:\/\/[^\s<]+|www\.[^\s<]+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:${FINAIS})\b(?:[\/?#][^\s<]*)?)`,
  'gi',
);

/**
 * Pontuação que costuma encostar no fim de um endereço dentro de uma frase.
 *
 * Inclui os marcadores do WhatsApp (#615): em `*veja https://exemplo.com*` o
 * asterisco fecha o negrito, não é do endereço. O `_` no MEIO do endereço
 * continua dele — só o do fim sai. Endereço de verdade terminado em `_` é raro
 * o bastante para não valer um link errado em todo itálico.
 */
const PONTUACAO_FINAL = /[.,;:!?)\]}'"»*_~`]+$/;

/**
 * Corta a pontuação da frase, preservando parêntese que é DO endereço.
 *
 * A Wikipédia produz `…/Fulano_(cantor)`, e cortar o fecha-parêntese ali
 * levaria a uma página que não existe. A regra: só corta o fecha se não houver
 * um abre correspondente dentro do próprio endereço.
 */
export function aparar(bruto: string): { url: string; sobra: string } {
  let url = bruto;
  let sobra = '';

  for (;;) {
    const encontrado = PONTUACAO_FINAL.exec(url);
    if (!encontrado) break;

    const candidato = encontrado[0];
    // Um fecha-parêntese equilibrado pertence ao endereço.
    if (candidato === ')' && (url.match(/\(/g) ?? []).length >= (url.match(/\)/g) ?? []).length) {
      break;
    }

    url = url.slice(0, encontrado.index);
    sobra = candidato + sobra;
  }

  return { url, sobra };
}

/** O `href` de verdade: `www.` seco precisa de esquema para não virar caminho relativo. */
export function enderecoNavegavel(url: string): string | null {
  const completo = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  try {
    const analisado = new URL(completo);
    // Cinto e suspensório: o `www.` acima poderia esconder algo como
    // `www.x@javascript:…`, e quem decide o esquema é o analisador, não a regex.
    if (analisado.protocol !== 'http:' && analisado.protocol !== 'https:') return null;
    return analisado.href;
  } catch {
    return null;
  }
}

/** Os pedaços do texto: string é texto comum, objeto é endereço. */
export function partir(
  texto: string,
): readonly (string | { readonly url: string; readonly href: string })[] {
  const pedacos: (string | { url: string; href: string })[] = [];
  let cursor = 0;

  // `matchAll` com a regex global — `exec` em laço guardaria `lastIndex` entre
  // chamadas e pularia endereços quando a mesma regex fosse reusada.
  for (const encontrado of texto.matchAll(ENDERECO)) {
    const inicio = encontrado.index;
    const { url, sobra } = aparar(encontrado[0]);
    const href = enderecoNavegavel(url);

    if (!href || url === '') continue;

    if (inicio > cursor) pedacos.push(texto.slice(cursor, inicio));
    pedacos.push({ url, href });
    cursor = inicio + url.length;
    if (sobra) {
      pedacos.push(sobra);
      cursor += sobra.length;
    }
  }

  if (cursor < texto.length) pedacos.push(texto.slice(cursor));
  return pedacos;
}
